import { LinkButton } from '@atlaskit/button/new';
import SectionMessage from '@atlaskit/section-message';
import Info from '@atlaskit/icon/glyph/info';
import { colors } from '@atlaskit/theme';
import Button from '@atlaskit/button';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import config from 'appconfig';
import { State } from 'model/State';
import { RESTRICTED_COUNTRIES_ISO_CODES, RESTRICTED_EMAIL_DOMAINS } from 'modules/utils';
import { extractTLDFrom } from 'modules/siteconfigurator/utils';
import { ErrorMessage, Form } from 'modules/stripe-credit-card';
import { useStripeOnSubmitMAC } from 'util/mac/stripeUtil';
import t from 'modules/i18n/intl';

import { selectCountryByName } from '../duck/selectors';
import { ContactDetailsFormSection } from './contactDetails/MACContactDetailsFormSection';
import { CCFormWrapper, PageH3Header } from './styledComponents';
import { fetchValidateCountryChange } from '../duck/actions/contact';
import { postUpdateCCDetails } from '../duck/actions/creditCard';

export type SelectOption = {
  label: string;
  value: string;
};

const InfoIcon = () => <Info label="Info icon" primaryColor={colors.B500} />;

const link = (chunks) => (
  <a
    data-link="customer-agreement"
    href={config.legalCustomerAgreement}
    target="_blank"
    title="Atlassian Customer Agreement"
    rel="noreferrer"
  >
    {chunks}
  </a>
);

export const UpdatePaymentDetailsForm = ({
  accountId,
  endDate,
  shouldShowPaymentDetailsForm,
  countryDetails,
  allCountries,
  states,
  billingDetailsForm,
  customerDetailsForContact,
  contactEmail,
  setBillingDetailsForm,
  setIsCountrySelectChanged,
  selectValidateCountry,
}) => {
  const dispatch = useDispatch();

  const [isRuBy, setIsRuBy] = useState(true);
  const [countryOptions, setCountryOptions] = useState<SelectOption[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(0);
  const [currencyChangeCountry, setCurrencyChangeCountry] = useState(false);

  const currentAccountCountryDetails = useSelector((state: State) =>
    selectCountryByName(state, customerDetailsForContact?.organisationDetails.country),
  );

  useEffect(() => {
    const isISORestricted = [
      currentAccountCountryDetails,
      countryDetails?.countryIsoCode,
    ].some((item) => RESTRICTED_COUNTRIES_ISO_CODES.includes(item));

    const isEmailRestricted = RESTRICTED_EMAIL_DOMAINS.includes(extractTLDFrom(contactEmail));

    if (isISORestricted || isEmailRestricted) {
      setIsRuBy(true);
    } else {
      setIsRuBy(false);
    }
  }, [currentAccountCountryDetails, countryDetails, contactEmail]);

  useEffect(() => {
    if (allCountries?.length) {
      setCountryOptions(
        allCountries.map(({ countryIsoCode, displayName }) => ({
          label: displayName,
          value: countryIsoCode,
        })),
      );
    }
  }, [allCountries?.length]);

  const handleFormChange = (e, field) => {
    setBillingDetailsForm({
      ...billingDetailsForm,
      [field]: e.currentTarget.value,
    });
  };

  const handleFormCountrySelectChange = (e) => {
    setBillingDetailsForm({
      ...billingDetailsForm,
      country: e,
      state: { label: '', value: '' },
      vatResponsible: null,
    });
    setIsCountrySelectChanged(true);
    dispatch(fetchValidateCountryChange(e.value));
  };

  const handleFormStateSelectChange = (e) => {
    setBillingDetailsForm({
      ...billingDetailsForm,
      state: e,
      taxId: '',
    });
  };

  const handleStripeSubmit = useStripeOnSubmitMAC(() => {
    if (isRuBy) return;
    dispatch(postUpdateCCDetails(accountId));
  });

  useEffect(() => {
    if (selectValidateCountry?.irreconcilable.length) {
      setCurrencyChangeCountry(true);
    } else {
      setCurrencyChangeCountry(false);
    }
  }, [selectValidateCountry?.irreconcilable.length]);

  const currencyChangeCountryMessageDetails = {
    defaultMessage:
      'Can not set payment details. The change to {inputtedCountry} would result in a change of currency on your account. Please {contactCustomerSupport} to change your billing country.',
    id: 'mac.payment-details.currency-change',
    values: {
      contactCustomerSupport: (
        <a href={config.companyContact}>
          <FormattedMessage
            defaultMessage="contact Customer Support"
            id="mac.payment-details.contact-customer-support"
          />
        </a>
      ),
      inputtedCountry: billingDetailsForm.country.label,
    },
  };

  return (
    <form
      onSubmit={(event) => {
        setIsSubmitted(isSubmitted + 1);
        handleStripeSubmit(event);
      }}
      data-cy="billing-detail-form"
    >
      {shouldShowPaymentDetailsForm && (
        <>
          <PageH3Header>
            <FormattedMessage
              defaultMessage="Billing Details"
              id="mac.payment-details.billing-details.header"
            />
          </PageH3Header>
          <p>
            <FormattedMessage
              defaultMessage="This will appear in your invoice"
              id="mac.payment-details.billing-details.description"
            />
          </p>
          <ContactDetailsFormSection
            countryDetails={countryDetails}
            billingDetailsForm={billingDetailsForm}
            states={states}
            countryOptions={countryOptions}
            currencyChangeCountry={currencyChangeCountry}
            currencyChangeCountryMessageDetails={currencyChangeCountryMessageDetails}
            handleFormChange={handleFormChange}
            handleFormStateSelectChange={handleFormStateSelectChange}
            handleFormCountrySelectChange={handleFormCountrySelectChange}
            setBillingDetailsForm={setBillingDetailsForm}
          />
        </>
      )}
      <PageH3Header>
        <FormattedMessage
          defaultMessage="Credit card details"
          id="mac.payment-details.cc-details.header"
        />
      </PageH3Header>
      <p>
        <FormattedMessage
          defaultMessage="This credit card will be used for your recurring payments"
          id="mac.payment-details.cc-details.description"
        />
      </p>
      <br />
      <CCFormWrapper>
        <Form autoFocus={false} frameEmbeddingMitigation={false} />
        <ErrorMessage />
      </CCFormWrapper>
      {endDate && endDate < new Date() && (
        <p>
          <SectionMessage appearance="information" icon={InfoIcon}>
            <FormattedMessage
              defaultMessage="As your subscription has expired, your credit card will be processed shortly for the next renewal. Atlassian will also use these credit card details to automatically renew your subscription going forward."
              id="mac.payment-details.expired-subscription-message"
            />
          </SectionMessage>
        </p>
      )}
      <p>
        {t(
          {
            defaultMessage:
              'By clicking here you accept the <link>Atlassian Customer Agreement</link>.',
            id: 'mac.payment-details.customer-agreement',
          },
          {
            link,
          },
        )}
      </p>
      <br />
      <Button
        data-cy="update-payment-details-submit-button"
        appearance="primary"
        type="submit"
        isDisabled={
          !customerDetailsForContact ||
          isRuBy ||
          !!isSubmitted ||
          currencyChangeCountry ||
          !billingDetailsForm.firstName ||
          !billingDetailsForm.lastName ||
          !billingDetailsForm.companyName ||
          !billingDetailsForm.address1 ||
          !billingDetailsForm.city ||
          !billingDetailsForm.state.value ||
          !billingDetailsForm.country.value
        }
      >
        <FormattedMessage defaultMessage="Update" id="mac.payment-details.button.update" />
      </Button>{' '}
      <LinkButton data-cy="update-payment-details-cancel-button" href="/purchase/my">
        <FormattedMessage defaultMessage="Cancel" id="mac.update-cc-page.button.cancel" />
      </LinkButton>
    </form>
  );
};
